import type { PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";
import z from "zod";

import { MissingDateOfBirthOrGenderModal } from "@/app/components/modals/missing-date-of-birth-or-gender-modal";
import { BASE_ROUTE } from "@/app/router";
import { usePortalUserStore } from "@/app/stores";
import { EmbeddablesMandatorySyncModal } from "@/features/embeddables/sections";
import { PendingRefillRequestModal } from "@/features/my-meds/components";
import { BlackFridayBanner } from "@/features/offers/components";
import { ScrollArea } from "@/shared/ui";
import { Heading } from "@/shared/ui/common/heading";

const layoutVariance = tv({
  slots: {
    base: "flex flex-col bg-white",
    container: "flex h-full grow flex-col",
    content:
      "flex grow flex-col gap-7 p-6 has-[.notification-bar]:pt-0 md:px-16 md:py-12",
    header: "pt-14 text-center text-neutral-950",
    paragraph:
      "pt-2 text-sm font-medium text-neutral-500 sm:text-lg sm:leading-6",
  },
  variants: {
    hasCardHeader: {
      false: {
        base: "md:pt-3",
        header: "pt-6 md:pt-14",
      },
    },
  },
  compoundVariants: [
    {
      variant: "dark",
      hasCardHeader: false,
      className: {
        header: "mx-auto max-w-3xl px-6 md:px-12",
      },
    },
  ],
  defaultVariants: {
    hasCardHeader: true,
  },
});

type LayoutVariance = VariantProps<typeof layoutVariance>;

interface ScreenLayoutProps extends PropsWithChildren, LayoutVariance {
  className?: string;
  title?: string;
  description?: string;
}

export const ScreenLayout = ({
  children,
  className,
  title,
  description,
  hasCardHeader,
}: ScreenLayoutProps) => {
  const isBlackFridayActive = usePortalUserStore(
    (state) => state.isBlackFridayActive,
  );
  const { base, header, paragraph, content, container } = layoutVariance({
    hasCardHeader,
  });

  const { pathname } = useLocation();

  const isAtBaseRoute = z.enum(BASE_ROUTE).safeParse(pathname).success;

  const showBlackFridayBanner = isBlackFridayActive && isAtBaseRoute;

  return (
    <ScrollArea className={base()}>
      {showBlackFridayBanner && <BlackFridayBanner />}

      <div className={container()}>
        {Boolean(title ?? description) && (
          <div className={header()}>
            <Heading level={1} size="large">
              {title}
            </Heading>
            <p className={paragraph()}>{description}</p>
          </div>
        )}

        <div className={content({ className })}>{children}</div>
      </div>

      <PendingRefillRequestModal />
      <MissingDateOfBirthOrGenderModal />
      <EmbeddablesMandatorySyncModal />
    </ScrollArea>
  );
};
